import { useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { DocumentTemplateView } from '@deltek/specification-client/core/Models/documentTemplate'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    closestCorners,
    DndContext,
    DragEndEvent,
    DragMoveEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardSensor,
    MouseSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { Typography } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import OfflineBoltOutlinedIcon from '@material-ui/icons/OfflineBoltOutlined'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    isOmWorkspaceUser,
    isWorkspaceEditable,
    setIsAddOngoing,
    setIsAddOnQueue,
    setIsAddProhibited,
    setIsCancelAddDialogOpen,
    setWorkspaceHasError,
    workspaceHasError,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { showHideToggles } from 'api/graphQL/reactiveVars/workspaceShowHideToggles'
import { workspaceToggles } from 'api/graphQL/reactiveVars/workspaceToggles'
import { processEnv } from 'components/configuration/environmentVariables'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import { levelOfSpecificationColor } from 'pages/ProjectsPage/colorVars'
import { ProjectPageContext } from 'pages/ProjectsPage/ProjectPage/components/ProjectPageContext'
import React from 'react'
import {
    canElementBeShown,
    isAllElementDeleted,
} from 'shared/utils/bestPracticeUtils'
import SpecifierNote from '../SpecifierNote'
import { articleDefaultValue, isSustainabilityTopicContent } from './addUtils'
import { AreaContext } from './AreaContext'
import { DragDropAreaContext } from './DragDropAreaContext'
import { Draggable } from './Draggable'
import DraggableItem, { DRAGGABLE_ITEM_BORDER_RADIUS } from './DraggableItem'
import { useCustomTransition } from './hooks/useCustomTransition'
import {
    NormalizedDocumentTemplateViewItem,
    PromoteDemoteRequirements,
} from './model'
import { TabsContext } from './TabsContext'
import {
    disableArticleExpand,
    findItemInTemplates,
    getProductTypeInFamilyViewContentType,
} from './utils'

export interface DragDropAreaProps {
    parent?: NormalizedDocumentTemplateViewItem
    items: NormalizedDocumentTemplateViewItem[]
    baseNotes?: NormalizedDocumentTemplateViewItem[]
    part?: 'General' | 'Products' | 'Execution' | 'ContentGroup'
    handleDragEnd: (event: DragEndEvent) => void
    contentTeamFromStudioPage?: boolean
    isDraftMode?: boolean
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    selectedContent?: string
    setPromoteDemoteRequirements?: (input?: PromoteDemoteRequirements) => void
    setAreSharedElementsVisible: (value: boolean) => void
    tabSelected: number
    isAddProhibited?: boolean
    projectProductFamilyId?: string
    isAddOngoing?: boolean
    contentItemNodes?: Partial<ContentItem>[]
    dtv: Partial<DocumentTemplateView> | null
    isNewAddOnQueue?: boolean
    addQueueSib?: string[] | undefined
    elementIdOnEdit?: string | undefined
    familyAddedProductTypes?: ProjectElementNode[]
}

const useStyles = makeStyles((theme: Theme) => ({
    center: { textAlign: 'center' },
    sectionContainer: {
        padding: theme.spacing(0.5),
        paddingBottom: theme.spacing(2),
    },
    section: { fontSize: pxToRem(14) },
    divContainer: {
        width: '100%',
        borderRadius: DRAGGABLE_ITEM_BORDER_RADIUS,
    },
    iconOutlinedTitle: {
        cursor: 'pointer',
    },
    offlineBoltIconContainer: {
        position: 'static',
        height: pxToRem(6),
    },
    offlineBoltSVGIcon: {
        position: 'sticky',
        zIndex: 1,
        left: pxToRem(20),
        marginTop: pxToRem(-2),
    },
}))
/**
 * This contains the context for all the droppable and draggables
 * The parent props are loaded here
 * This will be the items on view with no parents
 * @param DragDropAreaProps.items
 * @param DragDropAreaProps.baseNotes
 * @param DragDropAreaProps.part
 * @param DragDropAreaProps.handleDragEnd
 */
const DragDropArea: React.FC<DragDropAreaProps> = ({
    parent,
    items,
    baseNotes,
    part,
    selectedContent,
    handleDragEnd,
    contentTeamFromStudioPage,
    setAreSharedElementsVisible,
    isDraftMode,
    selectedTopic,
    setPromoteDemoteRequirements,
    tabSelected,
    isAddProhibited,
    projectProductFamilyId,
    isAddOngoing,
    contentItemNodes,
    dtv,
    isNewAddOnQueue,
    addQueueSib,
    elementIdOnEdit,
    familyAddedProductTypes,
}) => {
    const { projectData } = React.useContext(ProjectPageContext)
    const isOMUser = useReactiveVar(isOmWorkspaceUser)
    const [_, startTransition] = useCustomTransition()

    const isAddingFailed = useReactiveVar(workspaceHasError)
    const isEditable = useReactiveVar(isWorkspaceEditable)
    const { isSystemUser, isAdmin } = useReactiveVar(userInfo)
    const { areSharedPropertiesVisible: areSharedVisible } = useReactiveVar(
        workspaceToggles
    )
    const { isTrackChangeToggleOn } = useReactiveVar(showHideToggles)
    const previousIsTrackChangeToggleOn = usePrevious(isTrackChangeToggleOn)
    const previousTab = usePrevious(tabSelected)
    const { familyProjectMetadata } = useReactiveVar(workspaceSelectedElement)

    const {
        selectedProjectNode,
        areHiddenPropertiesVisible,
        areNotesVisible,
        type,
        expandStateList,
        updateExpandState,
    } = React.useContext(AreaContext)
    const {
        handleDraggingId,
        handleRegroup,
        draggingId,
        tabData,
        setAutoCollapsedId,
        isRegroup,
    } = React.useContext(DragDropAreaContext)
    const { dragStarted, setDragStarted } = React.useContext(TabsContext)
    const isAEUserProjectWorkspace = React.useMemo(
        () =>
            !contentTeamFromStudioPage &&
            processEnv.REACT_APP_EnableVersionControl === '1' &&
            isOMUser,
        [contentTeamFromStudioPage, isOMUser, processEnv]
    )
    const draggingElement = React.useMemo(() => {
        if (!draggingId) return {} as NormalizedDocumentTemplateViewItem
        return findItemInTemplates(
            tabData?.children as NormalizedDocumentTemplateViewItem[],
            draggingId as string
        )
    }, [tabData, draggingId])

    const isMovable = React.useMemo(() => {
        if (!contentTeamFromStudioPage && isEditable) {
            return true
        }
        if (
            contentTeamFromStudioPage &&
            isDraftMode &&
            (isSystemUser || isOMUser) &&
            (!isAdmin || isOMUser)
        ) {
            return true
        }
        return false
    }, [isEditable, isSystemUser, contentTeamFromStudioPage, isAdmin, isOMUser])

    const [articles, setArticles] = React.useState<
        Partial<NormalizedDocumentTemplateViewItem>[]
    >([])

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(MouseSensor),
        useSensor(KeyboardSensor)
    )

    const handleBeforeDragStart = React.useCallback(
        (ev: DragStartEvent) => {
            const { active } = ev
            if (active?.data?.current?.disabled) return
            const id = active?.id?.substring(active?.id?.indexOf('-') + 1)
            handleDraggingId?.(id)
            setDragStarted?.(true)
            if (
                active?.data?.current?.sortable?.containerId.includes(
                    'dragDropArea'
                )
            ) {
                const template =
                    findItemInTemplates(
                        tabData?.children as NormalizedDocumentTemplateViewItem[],
                        id as string
                    ) ?? ({} as NormalizedDocumentTemplateViewItem)

                const articleId = template?.contentItem?.id
                if (articleId && expandStateList?.includes(articleId)) {
                    updateExpandState?.(articleId as string)
                    setAutoCollapsedId(articleId)
                }
            }
        },
        [
            handleDraggingId,
            setDragStarted,
            updateExpandState,
            setAutoCollapsedId,
            tabData,
            expandStateList,
        ]
    )

    const handleDragMove = React.useCallback(
        (ev: DragMoveEvent) => {
            const { delta, active, over } = ev

            if (
                delta?.x > 20 &&
                !active?.data?.current?.sortable?.containerId.includes(
                    'dragDropArea'
                ) &&
                draggingElement?.isUserAdded &&
                ((over?.data?.current?.sortable?.level &&
                    over?.data?.current?.sortable?.level < 6) ||
                    draggingElement?.level < 6)
            ) {
                handleRegroup?.(true)
            }
            if (isRegroup && delta?.x < 20) {
                handleRegroup?.(false)
            }
        },
        [handleRegroup, draggingElement, isRegroup]
    )

    const handleDragDrop = React.useCallback(
        (ev: DragEndEvent) => {
            handleDragEnd?.(ev)
            handleRegroup?.(false)
        },
        [handleDragEnd, handleRegroup]
    )
    const handleDragCancel = React.useCallback(() => {
        setDragStarted?.(false)
    }, [setDragStarted])

    const classes = useStyles()

    const addNewArticle = React.useCallback(
        (
            newArticle: NormalizedDocumentTemplateViewItem,
            indexPlacement?: number,
            articleIndex?: number
        ) => {
            const articles = [...items]
            const isSustainabilityContent = isSustainabilityTopicContent(
                items[0]
            )
            // Use articleIndex (actual array position) over indexPlacement (logical order)
            const insertPosition =
                typeof articleIndex === 'number'
                    ? articleIndex
                    : indexPlacement ?? items.length
            const lastParagraphOrder: number =
                items[insertPosition - 1]?.paragraphOrder ?? 1
            articles.splice(insertPosition, 0, {
                ...newArticle,
                id: !!isSustainabilityContent
                    ? newArticle?.id
                    : 'temporaryArticleTemplateId',
                partType: items?.[0].partType,
                paragraphOrder: lastParagraphOrder + 1,
                parentId: items?.[0].parentId,
                order: indexPlacement,
            })
            startTransition(() => {
                setArticles(
                    articles.map((item, index) => {
                        if (
                            typeof articleIndex === 'number' &&
                            index > insertPosition &&
                            item?.paragraphOrder
                        ) {
                            return {
                                ...item,
                                paragraphOrder: item?.paragraphOrder + 1,
                            }
                        } else {
                            return {
                                ...item,
                            }
                        }
                    })
                )
            }, 100)
            setIsAddProhibited?.(true)
        },
        [items]
    )

    const cancelAddArticle = React.useCallback(
        (removingLevel1ContentInSustainability?: boolean) => {
            let resultingArray = [...articles]
            if (removingLevel1ContentInSustainability) {
                resultingArray = articles.filter(
                    (child) => child?.id !== 'temporaryTemplateId'
                )
            } else {
                const newlyaddedIndex = articles.findIndex(
                    (child) => !!child.addingState
                )
                resultingArray = articles
                    .filter((child) => !!!child.addingState)
                    .map((child, index) => {
                        if (index >= newlyaddedIndex) {
                            return {
                                ...child,
                                paragraphOrder: child.paragraphOrder! - 1,
                            }
                        } else {
                            return {
                                ...child,
                                paragraphOrder: child.paragraphOrder,
                            }
                        }
                    })
            }

            setArticles(resultingArray)
            setIsAddProhibited?.(false)
            setIsAddOngoing?.(false)
            setIsAddOnQueue?.(false)
            // this is to bypass handle blur fn on text changes
            // this will stop property from adding when we type in some text and remove the property
            setIsCancelAddDialogOpen?.(true)

            if (isAddingFailed) {
                setWorkspaceHasError(false)
            }
        },
        [isAddingFailed, articles]
    )
    const markLastPropertyForForm = React.useCallback(
        (items: NormalizedDocumentTemplateViewItem[]) => {
            if (items[items.length - 1]?.children.length > 0) {
                markLastPropertyForForm(items[items.length - 1]?.children)
            } else {
                let lastProperty = items[items.length - 1]
                if (lastProperty) {
                    lastProperty.isLastPropertyForForm = true
                }
            }
        },
        []
    )

    React.useEffect(() => {
        if (
            (!areSharedVisible &&
                items.length <= articles.length &&
                !articles.some((article) =>
                    article.id?.includes('temporary')
                )) ||
            !articles.some((article) => article.id?.includes('temporary'))
        ) {
            setArticles(items)
        }
        if (
            !isAddProhibited &&
            articles.some((article) => article.id?.includes('temporary'))
        ) {
            setArticles(items)
        }

        if (
            items.length >= articles.length &&
            articles.some((article) => article.id?.includes('temporary'))
        ) {
            setArticles(items)
            setIsAddProhibited?.(false)
            markLastPropertyForForm(items)
        }

        if (previousTab !== tabSelected) {
            setArticles(items)
        }

        if (
            previousIsTrackChangeToggleOn != isTrackChangeToggleOn &&
            !isTrackChangeToggleOn
        ) {
            const newItems = items.filter((item) => isAllElementDeleted(item))
            setArticles(newItems)
        }
    }, [
        items,
        articles,
        markLastPropertyForForm,
        previousTab,
        tabSelected,
        areSharedVisible,
        isAddProhibited,
    ])

    React.useEffect(() => {
        if (
            isAddingFailed &&
            articles.some((article) => article.addingState === 'newlyAdded')
        ) {
            const newArticleIndex = articles.findIndex(
                (article) => article.addingState
            )

            setArticles(
                articles
                    ?.slice(0, newArticleIndex)
                    .concat({
                        ...articles[newArticleIndex],
                        contentItem: {
                            ...articles[newArticleIndex].contentItem,
                            elements: [
                                {
                                    text: articleDefaultValue,
                                },
                            ],
                        },
                        addingState: 'saved',
                        hasError: true,
                    })
                    .concat(articles?.slice(newArticleIndex + 1))
            )
        }
    }, [isAddingFailed, articles])

    const filteredArtileArray = React.useMemo(
        () =>
            articles.filter(
                (art) =>
                    art.contentItem?.textType !== 'OfficeMasterNote' &&
                    !art.isHidden &&
                    !(art.isDeactivated && isAEUserProjectWorkspace)
            ),
        [articles, isAEUserProjectWorkspace]
    )

    /**
     * Determines whether to render Level of Specification icon (offline bolt icon)
     *
     * @param {ProjectElementNode} selectedProjectNode - The selected project node.
     * @param {Partial<NormalizedDocumentTemplateViewItem>} item - The document template view of article.
     * @param {any} projectData - The project data.
     * @param {any} familyProjectMetadata - The updated projectMetadata of selected fammily element
     * @returns {boolean} True if the offline bolt icon should be rendered, otherwise false.
     */
    const shouldRenderOfflineBoltIcon = (
        selectedProjectNode: ProjectElementNode,
        item: Partial<NormalizedDocumentTemplateViewItem>,
        projectData: any,
        familyProjectMetadata: any
    ): boolean => {
        return (
            //Only show LOS icon when the selected element is in the project and
            //it is not a product type or product group
            selectedProjectNode?.isInProject &&
            !(
                item?.partType?.toLocaleLowerCase() === 'products' &&
                (!!item.isProductType ||
                    !!item?.isProductGroup ||
                    !!item?.isForProductType ||
                    !!item?.isProductGroupHasMultipleProductTypes)
            ) &&
            //If target LOS is manually updated, use the target LOS for to show LOS icon,
            //Other wise use the project LOS
            (familyProjectMetadata?.isTargetLevelOfSpecificationManuallyUpdated
                ? familyProjectMetadata?.targetLOD &&
                  Number(item?.levelOfSpecificationTag || '') >
                      Number(familyProjectMetadata?.targetLOD)
                : projectData?.getProject.levelOfSpecification &&
                  Number(item?.levelOfSpecificationTag || '') >
                      Number(
                          projectData?.getProject.levelOfSpecification || ''
                      )) &&
            !(contentTeamFromStudioPage && (isSystemUser || isOMUser))
        )
    }

    if (articles?.length === 0) return null

    return (
        <>
            {(part === 'General' ||
                (part === 'ContentGroup' &&
                    type === 'Sustainability Topic')) && (
                <div
                    className={
                        part === 'ContentGroup' ? '' : classes.sectionContainer
                    }
                >
                    {baseNotes
                        ?.filter(
                            (eachBaseNotes) =>
                                !eachBaseNotes?.contentItem?.elements?.[0].text?.includes(
                                    'Copyright'
                                ) // Exclude text with copyright
                        )
                        .map((each) => (
                            <div className={classes.center} key={each.id}>
                                <Typography
                                    component="span"
                                    className={classes.section}
                                    data-testid={`spanBaseNotes${each.id}`}
                                >
                                    {each.contentItem?.elements
                                        ?.reduce(
                                            (result, eachElement) => [
                                                ...result,
                                                eachElement.text ?? '',
                                            ],
                                            [] as string[]
                                        )
                                        ?.join(' ')}
                                </Typography>
                            </div>
                        ))}
                </div>
            )}
            <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={isMovable ? handleBeforeDragStart : undefined}
                onDragEnd={isMovable ? handleDragDrop : undefined}
                onDragMove={handleDragMove}
                onDragCancel={handleDragCancel}
                autoScroll={true}
            >
                {articles?.map((item, index) => (
                    <>
                        {!(item?.isHidden && !areHiddenPropertiesVisible) &&
                            areNotesVisible && (
                                <GridItem
                                    key={`specifierNote-${item?.id}-${isDraftMode}`}
                                >
                                    <SpecifierNote
                                        key={`parentComment`}
                                        template={
                                            item as NormalizedDocumentTemplateViewItem
                                        }
                                        notes={
                                            item?.contentItem?.parentComments!
                                        }
                                        isHidden={false}
                                        isArticleLevelComment={true}
                                        contentTeamFromStudioPage={
                                            contentTeamFromStudioPage
                                        }
                                        editMode={
                                            isEditable &&
                                            contentTeamFromStudioPage &&
                                            isDraftMode
                                        }
                                    />
                                </GridItem>
                            )}
                        {canElementBeShown(item, isTrackChangeToggleOn) && (
                            <Draggable
                                key={`${item?.id}-${isDraftMode}`}
                                isWrapped={
                                    !contentTeamFromStudioPage
                                        ? !!selectedProjectNode?.elementId &&
                                          !item?.isHidden
                                        : isDraftMode
                                }
                                id={item.id as string}
                                index={index}
                                droppableId={'droppable-dragDropArea'}
                                isUserAdded={item.isUserAdded ?? false}
                            >
                                <DraggableItem
                                    losOfflineBoltIcon={
                                        areHiddenPropertiesVisible &&
                                        selectedProjectNode &&
                                        shouldRenderOfflineBoltIcon(
                                            selectedProjectNode,
                                            item,
                                            projectData,
                                            familyProjectMetadata
                                        ) ? (
                                            <Typography
                                                className={
                                                    classes.offlineBoltIconContainer
                                                }
                                                component="div"
                                                data-testid={`offlineBoltIcon`}
                                            >
                                                <HoverTooltip
                                                    text={`Activated at Level of Specification ${item?.levelOfSpecificationTag}`}
                                                >
                                                    <OfflineBoltOutlinedIcon
                                                        className={
                                                            (classes.iconOutlinedTitle,
                                                            classes.offlineBoltSVGIcon)
                                                        }
                                                        htmlColor={
                                                            levelOfSpecificationColor
                                                        }
                                                        fontSize="small"
                                                        width={'18px'}
                                                        height={'18px'}
                                                    />
                                                </HoverTooltip>
                                            </Typography>
                                        ) : null
                                    }
                                    template={
                                        item as NormalizedDocumentTemplateViewItem
                                    }
                                    parentType={item.id as string}
                                    contentItemIndex={filteredArtileArray.findIndex(
                                        (article) => article.id == item.id
                                    )}
                                    allContentItems={filteredArtileArray}
                                    disableArticleExpandInFullView={disableArticleExpand(
                                        item,
                                        selectedProjectNode?.elementType,
                                        contentTeamFromStudioPage,
                                        isOMUser
                                    )}
                                    isDraggable={
                                        !!selectedProjectNode?.elementId &&
                                        !disableArticleExpand(
                                            item,
                                            selectedProjectNode?.elementType,
                                            contentTeamFromStudioPage
                                        )
                                    }
                                    addProperty={addNewArticle}
                                    isPropertyShared={
                                        !item.isForProductType as boolean
                                    }
                                    cancelAddProperty={cancelAddArticle}
                                    droppableId={'droppable-dragDropArea'}
                                    index={index}
                                    isDraftMode={isDraftMode}
                                    contentTeamFromStudioPage={
                                        contentTeamFromStudioPage
                                    }
                                    selectedTopic={selectedTopic}
                                    selectedContent={selectedContent}
                                    setPromoteDemoteRequirements={
                                        setPromoteDemoteRequirements
                                    }
                                    setAreSharedElementsVisible={
                                        setAreSharedElementsVisible
                                    }
                                    isLastChildOfParent={
                                        index === (articles?.length ?? 1) - 1
                                    }
                                    parent={parent}
                                    projectProductFamilyId={
                                        projectProductFamilyId
                                    }
                                    isAddProhibited={isAddProhibited}
                                    isAddOngoing={isAddOngoing}
                                    contentItemNodes={contentItemNodes}
                                    dtv={dtv}
                                    isNewAddOnQueue={isNewAddOnQueue}
                                    addQueueSib={addQueueSib}
                                    elementIdOnEdit={elementIdOnEdit}
                                    tabSelected={tabSelected}
                                    productTypeInFamilyViewContentType={getProductTypeInFamilyViewContentType(
                                        item as NormalizedDocumentTemplateViewItem,
                                        selectedProjectNode?.elementType,
                                        contentTeamFromStudioPage,
                                        dtv?.items,
                                        familyAddedProductTypes
                                    )}
                                    familyAddedProductTypes={
                                        familyAddedProductTypes
                                    }
                                />
                            </Draggable>
                        )}
                    </>
                ))}

                <DragOverlay dropAnimation={null} style={{ height: '50px' }}>
                    {draggingId && dragStarted && draggingElement && (
                        <div
                            style={{
                                backgroundColor: 'rgba(0,0,0,0.25)',
                                opacity: '0.5',
                                width: '1000px',
                                height: '100%',
                            }}
                        >
                            &nbsp;
                        </div>
                    )}
                </DragOverlay>
            </DndContext>
        </>
    )
}

export default DragDropArea
